import { ArrowRight, Award, Cog, Factory, Users } from "lucide-react";
import PageHero from "@/components/PageHero";
import { Reveal, RevealText, SectionHeading } from "@/components/Reveal";
import { BlueprintGrid } from "@/components/Backdrop";
import PageLinkCTA from "@/components/PageLinkCTA";
import { COMPANY } from "@/data/company";

const FACTS = [
  { label: "Established", value: "2020" },
  { label: "Domain", value: "Railway Engineering" },
  { label: "Products", value: "Electrical & Electromechanical" },
  { label: "Model", value: "Manufacturing & Supply" },
  { label: "Approach", value: "Quality Driven" },
];

const PILLARS = [
  {
    icon: Award,
    title: "Quality",
    text: "Quality-focused manufacturing, from material selection to final finishing — because railway applications leave no room for shortcuts.",
  },
  {
    icon: Cog,
    title: "Technical Capability",
    text: "Practical engineering understanding of railway electrical, electromechanical and industrial components.",
  },
  {
    icon: Factory,
    title: "Manufacturing & Supply",
    text: "Component manufacturing combined with dependable supply for the Indian railway ecosystem.",
  },
  {
    icon: Users,
    title: "Customer Requirements",
    text: "We work to the customer's requirement — responsive, clear and dependable across every engagement.",
  },
];

const FACTORY_IMG = "https://images.unsplash.com/photo-1717386255773-1e3037c81788?crop=entropy&cs=srgb&fm=jpg&q=85";

export default function About() {
  return (
    <>
      <PageHero
        eyebrow="About Us"
        title="Engineering with Purpose. Manufacturing with Precision."
        description="BIK COMMOTRADE (OPC) PVT LTD — an engineering and railway-sector products company serving the Indian railway ecosystem."
      />

      <section className="relative section-pad bg-paper text-ink" data-testid="about-story">
        <BlueprintGrid light />
        <div className="wrap relative grid items-center gap-14 lg:grid-cols-[1.1fr_0.9fr]">
          <div>
            <SectionHeading dark={false} eyebrow="Who We Are" title="A Focused Railway Engineering Company" />
            <Reveal delay={0.15}>
              <div className="mt-6 space-y-4 leading-relaxed text-slate-700">
                <p>
                  BIK COMMOTRADE (OPC) PVT LTD is involved in manufacturing, supplying and developing railway
                  electrical, electromechanical, lighting, traction-support and industrial engineering components.
                </p>
                <p>
                  Established in {COMPANY.established}, the company approaches every product with the same
                  priorities: quality, reliability, technical capability and a clear understanding of customer
                  requirements.
                </p>
                <p>
                  We present ourselves as a professional railway vendor and engineering solutions company —
                  engineering for reliable railway applications, and building long-term relationships through
                  consistent, dependable delivery.
                </p>
              </div>
            </Reveal>
            <Reveal delay={0.25}>
              <div className="mt-8 grid gap-3 sm:grid-cols-2">
                {PILLARS.map((p) => (
                  <div
                    key={p.title}
                    className="group rounded-2xl border border-navy-700/10 bg-white p-5 shadow-sm transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_20px_50px_rgba(16,185,129,0.16)]"
                  >
                    <p.icon className="h-6 w-6 text-brand-emerald" />
                    <h3 className="mt-3 font-display text-lg font-bold uppercase tracking-wide">{p.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-slate-700">{p.text}</p>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.15} className="relative">
            <div className="relative overflow-hidden rounded-3xl shadow-[0_40px_90px_rgba(11,19,43,0.3)]">
              <img
                src={FACTORY_IMG}
                alt="Modern precision manufacturing unit"
                loading="lazy"
                className="aspect-[3/4] w-full object-cover lg:aspect-[4/5]"
              />
              <div aria-hidden="true" className="absolute inset-0 bg-gradient-to-t from-navy-950/70 via-transparent to-brand-blueGlow/15" />
              <div aria-hidden="true" className="absolute inset-x-0 bottom-0 h-1 bg-gradient-to-r from-brand-emerald via-brand-blueGlow to-brand-emerald" />
              <div className="absolute bottom-6 left-6 right-6">
                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-brand-emeraldDark">
                  Industrial Manufacturing
                </p>
                <p className="mt-2 font-display text-2xl font-bold uppercase leading-tight text-ink">
                  Precision on the factory floor
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="relative overflow-hidden bg-[#F3F7FB] section-pad" data-testid="about-facts">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 bg-[radial-gradient(50%_40%_at_50%_0%,rgba(16,185,129,0.1),transparent_70%)]"
        />
        <div className="wrap relative">
          <SectionHeading
            eyebrow="At a Glance"
            title="The Facts, Stated Simply"
            description="No inflated claims — just who we are, what we do and how we work."
          />
          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-5">
            {FACTS.map((f, i) => (
              <Reveal key={f.label} delay={i * 0.07} className="h-full">
                <div className="card-dark group h-full p-6 transition-all duration-500 hover:-translate-y-1.5 hover:border-brand-emeraldLight/40">
                  <p className="font-mono text-[10px] uppercase tracking-[0.26em] text-brand-blueDark">{f.label}</p>
                  <p className="mt-3 font-display text-xl font-bold uppercase leading-tight text-ink">{f.value}</p>
                  <span
                    aria-hidden="true"
                    className="mt-4 block h-[2px] w-8 bg-gradient-to-r from-brand-emerald to-brand-blueGlow transition-all duration-500 group-hover:w-full"
                  />
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.3}>
            <div className="mt-14 text-center">
              <RevealText
                text="Engineering for reliable railway applications."
                as="p"
                className="font-display text-2xl font-semibold uppercase tracking-wide text-slate-600 sm:text-3xl"
              />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="wrap section-pad" data-testid="about-cta">
        <PageLinkCTA />
      </section>
    </>
  );
}
